/**
 * Maps fleet-doctor menu item names to lucide-react icon components.
 * This provides the ts-main visual style while using fleet-doctor's dynamic menu data.
 */
import {
  Home,
  Ship,
  Wrench,
  Cog,
  Users,
  UserCheck,
  ShieldCheck,
  ShoppingCart,
  Package,
  DollarSign,
  Calculator,
  Building2,
  LayoutDashboard,
  FileText,
  MapPin,
  ClipboardCheck,
  AlertTriangle,
  Eye,
  BookOpen,
  BarChart3,
  Truck,
  Warehouse,
  Receipt,
  CreditCard,
  Banknote,
  Calendar,
  Clock,
  Navigation,
  Mail,
  User,
  UserPlus,
  Lock,
  Key,
  ShieldAlert,
  Check,
  Fuel,
  BadgeCheck,
  IdCard,
  type LucideIcon,
} from 'lucide-react'

// Fallback icon for unmapped items
const DefaultIcon = FileText

/**
 * Top-level module icons — maps module/menu group names to lucide icons.
 * These are the main sidebar navigation icons.
 */
export const MODULE_ICONS: Record<string, LucideIcon> = {
  Home: Home,
  Dashboard: LayoutDashboard,
  Operations: Ship,
  Technical: Wrench,
  Crewing: Users,
  QHSE: ShieldCheck,
  Procurement: ShoppingCart,
  Accounts: Calculator,
  Admin: Building2,
  Management: Building2,
}

/**
 * Leaf-level (submenu item) icons — maps specific page/feature names to lucide icons.
 * Used for submenu items inside each module.
 */
export const LEAF_ICONS: Record<string, LucideIcon> = {
  // Operations
  'Voyage Reports': FileText,
  'Position Reports': MapPin,
  'Position Report New': MapPin,
  'Daily Progress Reports': MapPin,
  'Cargo Operations': Package,
  'Library': BookOpen,
  'In-port Consumption Report': Fuel,
  'Off Shore Report': Navigation,
  'Form Index': ClipboardCheck,
  'Reports': BarChart3,
  'Defects List': AlertTriangle,

  // Technical
  'Machinery': Cog,
  'PMS': Calendar,
  'Maintenance Plans': Calendar,
  'Running Hours': Clock,
  'Job List': ClipboardCheck,
  'Equipment': Wrench,
  'Forms & Checklists': FileText,
  'Components': Wrench,
  'Parts': Package,
  'Defects': AlertTriangle,

  // Crewing
  'Crew Master': User,
  'Crew Assignment': UserCheck,
  'Crew Resthours and Overtime': Clock,
  'Crew Overtime': Clock,
  'Crew Deduction': DollarSign,
  'Crew Claims and Reimbursement': Receipt,
  'Crew Allotment and Advance': Banknote,
  'Crew Training, Test & Verification': BadgeCheck,
  'Crew Appraisal': IdCard,
  'Crew Planning': Calendar,
  'Crew Change': UserPlus,
  'Crew List': Users,

  // QHSE
  'Incidents': AlertTriangle,
  'Audits': ShieldCheck,
  'Audits and Inspection': ShieldCheck,
  'Audits & Inspection': ShieldCheck,
  'Inspections': Eye,
  'Risk Assessment': ShieldAlert,
  'Safety Reports': FileText,
  'Submissions': ClipboardCheck,
  'Permits': Lock,
  'Static Templates': FileText,

  // Procurement
  'Overview': LayoutDashboard,
  'Requisitions': FileText,
  'RFQ Management': Mail,
  'Quotation Management': Receipt,
  'Purchase Orders': ShoppingCart,
  'Procurement Trail': Navigation,
  'Goods Receipt': Truck,
  'Invoice Management': CreditCard,
  'Credit Note': Banknote,
  'Approval Workflow': Check,
  'Approval Limits': BookOpen,
  'Audit Trail': Eye,
  'User Management': Users,
  'Vendors': Building2,
  'Inventory': Warehouse,
  'Approvals': Check,

  // Accounts
  'General Ledger': BookOpen,
  'Invoices': Receipt,
  'Payments': CreditCard,
  'Budget': Calculator,

  // Admin / Management
  'Companies': Building2,
  'Sub-Companies': Building2,
  'Vessels': Ship,
  'Vendor Management': Building2,
  'Access Management': Lock,
  'Roles': Key,
}

/**
 * Resolves a lucide icon for a given menu item name.
 * Falls back to module-level icon, then to FileText.
 */
export function resolveIcon(itemName: string, moduleName?: string): LucideIcon {
  // 1. Exact leaf match
  if (LEAF_ICONS[itemName]) return LEAF_ICONS[itemName]

  // 2. Normalized match (lowercase, strip punctuation)
  const normalized = itemName.toLowerCase().replace(/[^a-z0-9]/g, '')
  for (const [key, icon] of Object.entries(LEAF_ICONS)) {
    if (key.toLowerCase().replace(/[^a-z0-9]/g, '') === normalized) {
      return icon
    }
  }

  // 3. Module-level match
  if (moduleName && MODULE_ICONS[moduleName]) return MODULE_ICONS[moduleName]
  if (MODULE_ICONS[itemName]) return MODULE_ICONS[itemName]

  // 4. Fallback
  return DefaultIcon
}

export { DefaultIcon }
